import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { IconButton, Box, useMediaQuery, useTheme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";

import DataTable from "components/DataTable";
import PageHeader from "components/PageHeader";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import Swal from "sweetalert2";

// Custom hooks
import useWelfareData from "layouts/tables/data/singleWelfareTableData";
import { API_BASE, API_HOST } from "config/api";

function Welfare() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [paymentType, setPaymentType] = React.useState("welfare");

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchWelfareData,
    handleExport,
    handleTypeChange,
    selectedMember,
    showMemberDetails,
    closeMemberDetails,
    churchId,
    MemberDetailsModal,
  } = useWelfareData();

  // Payment modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState({
    type: "welfare",
    amount: "",
    method: "paystack",
    reference: `WELFARE-${Date.now().toString().slice(-6)}`,
    paymentDate: new Date().toISOString().split("T")[0],
    status: "active",
    email: "",
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Mobile states
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "amount"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("all");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchWelfareData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "all":
        setDateRange({ startDate: startOfDay(today), endDate: endOfDay(today) });
        fetchWelfareData(null, null, paymentType);
        return;
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchWelfareData(startDate, endDate, paymentType);
  };

  const handlePaymentTypeChange = (event) => {
    const type = event.target.value;
    setPaymentType(type);
    handleTypeChange(type);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  // Payment Modal Handlers
  const handleAddPayment = () => {
    setSelectedPayment({
      type: "welfare",
      amount: "",
      method: "paystack",
      reference: `WELFARE-${Date.now().toString().slice(-6)}`,
      paymentDate: new Date().toISOString().split("T")[0],
      status: "active",
      email: "",
    });
    setPaymentModalOpen(true);
  };

  const handlePaystackPayment = useCallback(async () => {
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Please login to continue");

      const userData = localStorage.getItem("user");
      if (!userData) {
        throw new Error("User data not found. Please login again.");
      }

      const user = JSON.parse(userData);
      const memberId = user.member_id;

      if (!memberId) {
        throw new Error("Member ID not found. Please login again.");
      }

      const requestBody = {
        member_id: memberId,
        email: selectedPayment.email,
        amount: parseFloat(selectedPayment.amount),
        type: selectedPayment.type || "welfare",
        church_id: user.church_id || null,
      };

      console.log("Initializing Paystack welfare payment:", requestBody);

      const response = await fetch(`${API_BASE}/payments/paystack/initialize`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseText = await response.text();
      console.log("Paystack response:", responseText);

      if (!response.ok) {
        let errorMessage = `Payment initialization failed: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = JSON.parse(responseText);

      if (data.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
      } else {
        throw new Error("No payment URL received from Paystack");
      }
    } catch (error) {
      console.error("Paystack welfare payment failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to initialize Paystack payment",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedPayment]);

  // Update reference when modal opens
  useEffect(() => {
    if (paymentModalOpen && !selectedPayment?.reference) {
      setSelectedPayment((prev) => ({
        ...prev,
        reference: `WELFARE-${Date.now().toString().slice(-6)}`,
      }));
    }
  }, [paymentModalOpen, selectedPayment]);

  // Mobile filter component
  const MobileFilters = () => (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        p: 2,
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={() => setShowMobileFilters(false)} size="small">
          <Icon>close</Icon>
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            value={dateRangeType}
            onChange={handleDateRangeTypeChange}
            label="Date Range"
          >
            <MenuItem value="all">All Records</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </TextField>
        </Grid>

        {dateRangeType === "custom" && (
          <>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  maxDate={dateRange.endDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To Date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  minDate={dateRange.startDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            value={paymentType}
            onChange={handlePaymentTypeChange}
            label="Payment Type"
          >
            <MenuItem value="welfare">Welfare</MenuItem>
            <MenuItem value="tithe">Tithe</MenuItem>
            <MenuItem value="offering">Offering</MenuItem>
            <MenuItem value="donation">Donation</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Button fullWidth variant="outlined" onClick={handleTodayClick}>
            Today
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading Welfare...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <PageHeader
            title={`My Welfare Report - ${formatDisplayDate(dateRange.startDate)}${
              !isSameDay(dateRange.startDate, dateRange.endDate)
                ? ` to ${formatDisplayDate(dateRange.endDate)}`
                : ""
            }`}
            actions={
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddPayment}
                  size={isSmallMobile ? "small" : "medium"}
                  startIcon={<Icon>add</Icon>}
                >
                  {!isSmallMobile && "New Payment"}
                </Button>

                {/* Mobile Filter Toggle */}
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    minWidth: "auto",
                    px: 1,
                  }}
                >
                  <Icon>filter_list</Icon>
                </Button>

                {/* Desktop Filters */}
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    select
                    size="small"
                    value={dateRangeType}
                    onChange={handleDateRangeTypeChange}
                    sx={{ width: 120 }}
                  >
                    <MenuItem value="all">All Records</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </TextField>

                  {dateRangeType === "custom" && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="From"
                        value={dateRange.startDate}
                        onChange={handleStartDateChange}
                        maxDate={dateRange.endDate}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 120 }} />
                        )}
                      />
                      <DatePicker
                        label="To"
                        value={dateRange.endDate}
                        onChange={handleEndDateChange}
                        minDate={dateRange.startDate}
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ width: 120 }} />
                        )}
                      />
                    </LocalizationProvider>
                  )}

                  <TextField
                    select
                    size="small"
                    value={paymentType}
                    onChange={handlePaymentTypeChange}
                    sx={{ width: 100 }}
                  >
                    <MenuItem value="welfare">Welfare</MenuItem>
                    <MenuItem value="tithe">Tithe</MenuItem>
                    <MenuItem value="offering">Offering</MenuItem>
                    <MenuItem value="donation">Donation</MenuItem>
                  </TextField>

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleTodayClick}
                    size="small"
                    sx={{ minWidth: "80px" }}
                  >
                    Today
                  </Button>

                  {/* Export Buttons - Icon only on mobile */}
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleExport("csv")}
                    size={isSmallMobile ? "small" : "medium"}
                    startIcon={<Icon>download</Icon>}
                  >
                    {!isSmallMobile && "CSV"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleExport("pdf")}
                    size={isSmallMobile ? "small" : "medium"}
                    startIcon={<Icon>picture_as_pdf</Icon>}
                  >
                    {!isSmallMobile && "PDF"}
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleExport("print")}
                    size={isSmallMobile ? "small" : "medium"}
                    startIcon={<Icon>print</Icon>}
                  >
                    {!isSmallMobile && "Print"}
                  </Button>
                </Box>
              </>
            }
          />

          <Card>
            {/* Mobile Filters Dropdown */}
            {showMobileFilters && <MobileFilters />}

            <Box pt={3}>
              <DataTable
                table={{
                  columns,
                  rows: rows.map((row) => ({
                    ...row,
                    originalData: row.originalData || {
                      id: row.id,
                      memberName: row.memberName,
                      type: row.type,
                      amount: row.amount,
                      method: row.method,
                      paymentDate: row.paymentDate,
                    },
                  })),
                }}
                isSorted={false}
                entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                showTotalEntries
                noEndBorder
                canSearch
                searchLabel="Search welfares..."
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <MemberDetailsModal
        member={selectedMember}
        open={showMemberDetails}
        onClose={closeMemberDetails}
        churchId={churchId}
      />

      {/* Add Payment Modal */}
      <FormDialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title="Create New Welfare Payment"
        onSave={handlePaystackPayment}
        saveLabel="Pay Via Paystack"
        saveDisabled={!selectedPayment?.amount || !selectedPayment?.email}
        loading={isProcessingPayment}
        maxWidth="md"
      >
        <FormSection>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Payment Type"
                value={selectedPayment?.type || "welfare"}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    type: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                required
              >
                <MenuItem value="welfare">Welfare</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Amount"
                type="number"
                value={selectedPayment?.amount || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    amount: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Payment Method"
                value="paystack"
                fullWidth
                margin="normal"
                required
                disabled
              >
                <MenuItem value="paystack">Paystack</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Reference Number"
                value={selectedPayment?.reference || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    reference: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: !!selectedPayment?.reference,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                value={selectedPayment?.email || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    email: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date"
                type="date"
                value={selectedPayment?.paymentDate || new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    paymentDate: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Status"
                value={selectedPayment?.status || "active"}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    status: e.target.value,
                  })
                }
                fullWidth
                margin="normal"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </FormSection>
      </FormDialog>
    </Box>
  );
}

export default Welfare;
